import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Inbox, Clock, CheckCheck, Gauge, TrendingUp, ArrowUpRight,
    MessageSquareText, Info, FileSpreadsheet, Building2, Plus,
    MessageSquare, Settings as SettingsIcon, ChevronRight, Home as HomeIcon,
    CalendarDays,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import AdminLayout from "./AdminLayout";
import VisitsByCountry from "./VisitsByCountry";
import { useI18n } from "../../i18n/I18nContext";
import { api } from "../../lib/api";

export default function AdminDashboard() {
    const { t, lang } = useI18n();
    const [stats, setStats] = useState(null);
    const [propsCount, setPropsCount] = useState(0);
    const [inquiries, setInquiries] = useState([]);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/admin/stats").then((r) => r.data).catch(() => null),
            api.get("/properties").then((r) => r.data.length).catch(() => 0),
            api.get("/admin/inquiries").then((r) => (r.data || []).slice(0, 5)).catch(() => []),
            api.get("/admin/messages").then((r) => (r.data || []).slice(0, 5)).catch(() => []),
        ])
            .then(([s, c, inq, msg]) => {
                setStats(s);
                setPropsCount(c);
                setInquiries(inq);
                setMessages(msg);
            })
            .finally(() => setLoading(false));
    }, []);

    const newInquiries = stats?.by_status?.new ?? 0;

    return (
        <AdminLayout>
            <div className="p-6 lg:p-10 space-y-10">
                <header className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <div className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-brand-gold mb-2">
                            {t("admin.dashboard")}
                        </div>
                        <h1 className="font-display text-4xl text-brand-navy">{t("cockpit.title")}</h1>
                        <p className="text-gray-500 mt-2">{t("cockpit.subtitle")}</p>
                    </div>
                    <div className="text-right hidden sm:block">
                        <div className="text-xs text-gray-500 tracking-wide uppercase">{lang === "en" ? "Listings" : "Biens en catalogue"}</div>
                        <div className="font-display text-3xl text-brand-navy">{propsCount}</div>
                    </div>
                </header>

                {loading ? (
                    <div className="text-center text-gray-500 py-24">Chargement…</div>
                ) : (
                    <>
                        {/* KPIs */}
                        {stats && (
                            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="cockpit-kpis">
                                <KpiCard icon={Gauge} accent="gold" label={t("cockpit.performance")} value={`${stats.performance}%`} progress={stats.performance} testid="kpi-performance" />
                                <KpiCard icon={CheckCheck} accent="emerald" label={t("cockpit.response_rate")} value={`${stats.response_rate}%`} progress={stats.response_rate} testid="kpi-response-rate" />
                                <KpiCard icon={TrendingUp} accent="navy" label={t("cockpit.processing_rate")} value={`${stats.processing_rate}%`} progress={stats.processing_rate} testid="kpi-processing-rate" />
                                <KpiCard icon={Clock} accent="cream" label={t("cockpit.avg_response")} value={`${stats.avg_response_hours}${t("cockpit.hours")}`} sub={`${t("cockpit.speed_score")} · ${stats.speed_score}%`} testid="kpi-avg-response" />
                            </section>
                        )}

                        {/* HUB */}
                        <section data-testid="admin-hub">
                            <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
                                <div>
                                    <div className="text-[0.65rem] tracking-[0.24em] uppercase font-bold text-brand-gold mb-1">
                                        {t("cockpit.hub_title")}
                                    </div>
                                    <h2 className="font-display text-2xl text-brand-navy">{t("cockpit.hub_subtitle")}</h2>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                <HubTile
                                    to="/admin/inquiries"
                                    icon={Inbox}
                                    label={t("cockpit.hub_inquiries")}
                                    sub={t("cockpit.hub_inquiries_sub")}
                                    accent="gold"
                                    badge={newInquiries > 0 ? String(newInquiries) : null}
                                    testid="hub-inquiries"
                                />
                                <HubTile
                                    to="/admin/bookings"
                                    icon={CalendarDays}
                                    label={t("bookings_admin.hub")}
                                    sub={t("bookings_admin.hub_sub")}
                                    accent="emerald"
                                    testid="hub-bookings"
                                />
                                <HubTile
                                    to="/admin/properties"
                                    icon={Building2}
                                    label={t("cockpit.hub_properties")}
                                    sub={t("cockpit.hub_properties_sub")}
                                    accent="navy"
                                    badge={String(propsCount)}
                                    testid="hub-properties"
                                />
                                <HubTile
                                    to="/admin/properties/new"
                                    icon={Plus}
                                    label={t("cockpit.hub_new_property")}
                                    sub={t("cockpit.hub_new_property_sub")}
                                    accent="emerald"
                                    testid="hub-new-property"
                                />
                                <HubTile
                                    to="/admin/messages"
                                    icon={MessageSquare}
                                    label={t("cockpit.hub_messages")}
                                    sub={t("cockpit.hub_messages_sub")}
                                    accent="cream"
                                    badge={messages.length > 0 ? String(messages.length) : null}
                                    testid="hub-messages"
                                />
                                <HubTile
                                    to="/admin/settings"
                                    icon={SettingsIcon}
                                    label={t("cockpit.hub_settings")}
                                    sub={t("cockpit.hub_settings_sub")}
                                    accent="navy"
                                    testid="hub-settings"
                                />
                                <HubTile
                                    to="/"
                                    icon={HomeIcon}
                                    label={lang === "en" ? "View public site" : "Voir le site public"}
                                    sub={lang === "en" ? "Preview visitor experience" : "Prévisualiser l'expérience visiteur"}
                                    accent="gold"
                                    testid="hub-public"
                                />
                            </div>
                        </section>

                        {/* CHART + STATUS */}
                        {stats && (
                            <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                                <div className="lg:col-span-2 bg-white border border-gray-100 p-6" data-testid="cockpit-chart">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <div className="text-[0.65rem] tracking-[0.24em] uppercase font-bold text-brand-gold">Analytics</div>
                                            <h3 className="font-display text-xl text-brand-navy">{t("cockpit.volume_14d")}</h3>
                                        </div>
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-500"><ArrowUpRight className="w-3.5 h-3.5" /> {stats.total} total</span>
                                    </div>
                                    <div className="h-56">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={stats.series_14d}>
                                                <defs>
                                                    <linearGradient id="colGold" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#C9A227" stopOpacity={0.5} />
                                                        <stop offset="100%" stopColor="#C9A227" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid stroke="#f1f1f1" vertical={false} />
                                                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#94a3b8" fontSize={11} />
                                                <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                                                <Tooltip contentStyle={{ fontSize: 12 }} />
                                                <Area type="monotone" dataKey="count" stroke="#C9A227" strokeWidth={2} fill="url(#colGold)" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                <div className="bg-brand-navy text-white p-6" data-testid="cockpit-statuses">
                                    <div className="text-[0.65rem] tracking-[0.24em] uppercase font-bold text-brand-gold mb-1">{lang === "en" ? "Distribution" : "Répartition"}</div>
                                    <h3 className="font-display text-xl text-white">{lang === "en" ? "By status" : "Par statut"}</h3>
                                    <ul className="mt-6 space-y-5">
                                        <StatusRow icon={Inbox} label={t("cockpit.new")} value={stats.by_status.new} total={stats.total} color="bg-brand-gold" />
                                        <StatusRow icon={Clock} label={t("cockpit.in_progress")} value={stats.by_status.in_progress} total={stats.total} color="bg-white/70" />
                                        <StatusRow icon={MessageSquareText} label={t("cockpit.responded")} value={stats.by_status.responded} total={stats.total} color="bg-brand-emerald" />
                                        <StatusRow icon={CheckCheck} label={t("cockpit.closed")} value={stats.by_status.closed} total={stats.total} color="bg-white/30" />
                                    </ul>
                                    <div className="mt-8 pt-6 border-t border-white/10">
                                        <div className="text-[0.65rem] tracking-[0.24em] uppercase font-bold text-brand-gold mb-3">{t("cockpit.by_type")}</div>
                                        <div className="flex gap-3">
                                            <TypeChip icon={Info} label={t("cockpit.info")} value={stats.by_type.info} />
                                            <TypeChip icon={FileSpreadsheet} label={t("cockpit.quote")} value={stats.by_type.quote} />
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* RECENT ACTIVITY + VISITS BY COUNTRY */}
                        <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div className="lg:col-span-2 space-y-5">
                                <ActivityPanel
                                title={t("cockpit.recent_inquiries")}
                                to="/admin/inquiries"
                                empty={t("cockpit.recent_empty")}
                                items={inquiries}
                                render={(it) => ({
                                    primary: it.name,
                                    secondary: it.property_title || (it.message || "").slice(0, 60),
                                    tag: it.type === "quote" ? t("cockpit.quote") : t("cockpit.info"),
                                    time: new Date(it.created_at).toLocaleDateString(),
                                    linkTo: "/admin/inquiries",
                                })}
                                testid="recent-inquiries"
                            />
                            <ActivityPanel
                                title={t("cockpit.recent_messages")}
                                to="/admin/messages"
                                empty={t("cockpit.recent_empty")}
                                items={messages}
                                render={(m) => ({
                                    primary: m.name,
                                    secondary: m.subject || (m.message || "").slice(0, 60),
                                    tag: null,
                                    time: new Date(m.created_at).toLocaleDateString(),
                                    linkTo: "/admin/messages",
                                })}
                                testid="recent-messages"
                            />
                            </div>
                            <VisitsByCountry />
                        </section>
                    </>
                )}
            </div>
        </AdminLayout>
    );
}

function KpiCard({ icon: Icon, label, value, sub, progress, accent = "gold", testid }) {
    const accents = {
        gold: "text-brand-gold bg-brand-gold/10",
        emerald: "text-brand-emerald bg-brand-emerald/10",
        navy: "text-brand-navy bg-brand-navy/10",
        cream: "text-brand-gold-deep bg-brand-cream",
    };
    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid={testid} className="bg-white border border-gray-100 p-6 relative overflow-hidden">
            <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 flex items-center justify-center ${accents[accent]}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
            </div>
            <div className="text-[0.65rem] tracking-[0.24em] uppercase font-bold text-gray-500">{label}</div>
            <div className="mt-1 font-display text-3xl text-brand-navy">{value}</div>
            {sub ? <div className="text-xs text-gray-500 mt-1">{sub}</div> : null}
            {progress != null && (
                <div className="mt-4 h-1 bg-gray-100 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(0, Math.min(100, progress))}%` }} transition={{ duration: 0.9 }} className={`h-full ${accent === "gold" ? "bg-brand-gold" : accent === "emerald" ? "bg-brand-emerald" : "bg-brand-navy"}`} />
                </div>
            )}
        </motion.div>
    );
}

function HubTile({ to, icon: Icon, label, sub, accent = "gold", badge, testid }) {
    const bg = {
        gold: "hover:bg-brand-gold hover:text-white",
        emerald: "hover:bg-brand-emerald hover:text-white",
        navy: "hover:bg-brand-navy hover:text-white",
        cream: "hover:bg-brand-cream",
    };
    const iconBg = {
        gold: "bg-brand-gold/10 text-brand-gold",
        emerald: "bg-brand-emerald/10 text-brand-emerald",
        navy: "bg-brand-navy/10 text-brand-navy",
        cream: "bg-brand-cream text-brand-gold-deep",
    };
    return (
        <Link
            to={to}
            data-testid={testid}
            className={`group bg-white border border-gray-100 p-6 flex items-start gap-4 transition-all duration-300 ${bg[accent]}`}
        >
            <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 transition-colors ${iconBg[accent]} group-hover:bg-white/15 group-hover:text-inherit`}>
                <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="font-display text-lg leading-tight">{label}</div>
                    {badge ? (
                        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 bg-brand-gold text-white text-[0.68rem] font-bold group-hover:bg-white group-hover:text-brand-navy transition-colors">
                            {badge}
                        </span>
                    ) : null}
                </div>
                <div className="text-xs opacity-70">{sub}</div>
            </div>
            <ChevronRight className="w-4 h-4 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
        </Link>
    );
}

function StatusRow({ icon: Icon, label, value, total, color }) {
    const pct = total ? Math.round((value / total) * 100) : 0;
    return (
        <li>
            <div className="flex items-center justify-between text-sm">
                <span className="inline-flex items-center gap-2">
                    <Icon className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
                    <span className="text-white/85">{label}</span>
                </span>
                <span className="text-white font-semibold">{value}</span>
            </div>
            <div className="mt-2 h-1 bg-white/10 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} className={`h-full ${color}`} />
            </div>
        </li>
    );
}

function TypeChip({ icon: Icon, label, value }) {
    return (
        <div className="flex-1 bg-white/5 px-3 py-3">
            <div className="inline-flex items-center gap-1.5 text-white/70">
                <Icon className="w-3.5 h-3.5 text-brand-gold" strokeWidth={1.5} />
                <span className="text-[0.65rem] tracking-[0.24em] uppercase font-bold">{label}</span>
            </div>
            <div className="mt-1 font-display text-2xl text-white">{value}</div>
        </div>
    );
}

function ActivityPanel({ title, to, empty, items, render, testid }) {
    const { t } = useI18n();
    return (
        <div className="bg-white border border-gray-100" data-testid={testid}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h3 className="font-display text-lg text-brand-navy">{title}</h3>
                <Link to={to} className="text-[0.65rem] tracking-[0.22em] uppercase font-semibold text-brand-navy hover:text-brand-gold transition-colors inline-flex items-center gap-1">
                    {t("cockpit.open")} <ChevronRight className="w-3.5 h-3.5" />
                </Link>
            </div>
            {items.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-gray-500">{empty}</div>
            ) : (
                <ul className="divide-y divide-gray-100">
                    {items.map((it, i) => {
                        const r = render(it);
                        return (
                            <li key={it.id || i} className="px-5 py-3 flex items-start justify-between gap-3 hover:bg-brand-paper/60 transition-colors">
                                <Link to={r.linkTo} className="flex-1 min-w-0 flex items-center gap-3">
                                    {r.tag ? (
                                        <span className="inline-block px-2 py-0.5 text-[0.6rem] tracking-[0.2em] uppercase font-bold bg-brand-gold/15 text-brand-gold-deep whitespace-nowrap">
                                            {r.tag}
                                        </span>
                                    ) : null}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-brand-navy truncate">{r.primary}</div>
                                        <div className="text-xs text-gray-500 truncate">{r.secondary}</div>
                                    </div>
                                </Link>
                                <div className="text-xs text-gray-400 whitespace-nowrap">{r.time}</div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
